import type { Coding } from '@medplum/fhirtypes'
import {
  type CodingAnswerOption,
  type CodingDetails,
  isScaleType,
  type ObservationTemplate,
} from '#src/codings.ts'
import { ASSESSMENT_ANSWER_CODES } from '#src/terminology/answerCodes.ts'
import { answerSetOptions } from '#src/terminology/answerSetOptions.ts'
import { ANSWER_SETS } from '#src/terminology/answerSets.ts'
import { conceptMappings } from '#src/terminology/conceptMappings.ts'
import {
  OBSERVATION_TEMPLATES,
  observationTemplate,
  templateDetails,
} from '#src/terminology/observationTemplates.ts'
import { ASSESSMENT_QUESTION_CODES } from '#src/terminology/questionCodes.ts'
import { STANDARD_CODE_MAP } from '#src/terminology/standardCodeMap.ts'
import {
  ASSESSMENT_QUESTIONS_SYSTEM,
  type TerminologyLibrary,
} from '#src/terminology/types.ts'

// Our terminology as the code defines it, capture properties included. Every
// environment reads this, and the same resources are written to Medplum at
// init so terminology operations ($lookup, $expand) answer for our codes.
export const TERMINOLOGY_LIBRARY: TerminologyLibrary = {
  questionCodes: ASSESSMENT_QUESTION_CODES,
  answerCodes: ASSESSMENT_ANSWER_CODES,
  answerSets: ANSWER_SETS,
  conceptMap: STANDARD_CODE_MAP,
}

// How one of our own question codes is captured, read from the code itself
// rather than asked of the terminology server. Undefined for a code that is
// not ours or cannot be recorded on its own.
export function libraryCodingDetails(
  coding: Coding,
  library: TerminologyLibrary = TERMINOLOGY_LIBRARY,
  templates: readonly ObservationTemplate[] = OBSERVATION_TEMPLATES
): CodingDetails | undefined {
  if (coding.system !== ASSESSMENT_QUESTIONS_SYSTEM || !coding.code) {
    return undefined
  }
  const concept = library.questionCodes.concept?.find(
    (candidate) => candidate.code === coding.code
  )
  // The parts of a code recorded as components come from its template.
  const template = observationTemplate(coding, templates)
  if (concept && template) {
    return {
      ...templateDetails(template),
      display: concept.display ?? concept.code,
    }
  }
  const property = (code: string): string | undefined =>
    concept?.property?.find((candidate) => candidate.code === code)?.valueString
  const scaleType = property('SCALE_TYP')
  if (!concept || !scaleType || !isScaleType(scaleType)) {
    return undefined
  }
  const answerList = property('answer-list')
  const referenceResourceType = concept.property?.find(
    (candidate) => candidate.code === 'reference-resource-type'
  )?.valueCode
  const valueKind = concept.property?.find(
    (candidate) => candidate.code === 'value-kind'
  )?.valueCode
  const details: CodingDetails = {
    scaleType,
    unit: (scaleType === 'Qn' && property('unit')) || null,
    display: concept.display ?? concept.code,
    answerOptions: answerList
      ? answerSetOptions(library, answerList).map(
          ({ coding: answer, score }) => {
            const option: CodingAnswerOption = {
              code: answer.code ?? '',
              display: answer.display ?? answer.code ?? '',
            }
            if (answer.system) {
              option.system = answer.system
            }
            if (score !== undefined) {
              option.score = score
            }
            return option
          }
        )
      : [],
  }
  if (
    scaleType === 'Qn' &&
    (valueKind === 'date' || valueKind === 'dateTime')
  ) {
    details.valueKind = valueKind
  }
  if (scaleType === 'Ref' && referenceResourceType) {
    details.referenceResourceType = referenceResourceType
  }
  if (
    concept.property?.some(
      (candidate) => candidate.code === 'repeats' && candidate.valueBoolean
    )
  ) {
    details.repeats = true
  }
  return details
}

// Every code an observation of this concept may have been recorded under:
// the code itself and the standard codes it is mapped to, in either
// direction. Codes a mapping marks as unmatched are left out.
export function observationCodeFamily(coding: Coding): Coding[] {
  const family: Coding[] = [coding]
  const add = (candidate: Coding): void => {
    if (
      !family.some(
        (known) =>
          known.system === candidate.system && known.code === candidate.code
      )
    ) {
      family.push(candidate)
    }
  }
  if (coding.system && coding.code) {
    for (const mapping of conceptMappings(
      TERMINOLOGY_LIBRARY,
      coding.system,
      coding.code
    )) {
      if (mapping.relationship !== 'unmatched' && mapping.code) {
        const mapped: Coding = { system: mapping.system, code: mapping.code }
        if (mapping.display) {
          mapped.display = mapping.display
        }
        add(mapped)
      }
    }
  }
  // A standard code maps back to each of our codes that names it.
  for (const group of TERMINOLOGY_LIBRARY.conceptMap.group ?? []) {
    if (group.target !== coding.system || !group.source) {
      continue
    }
    for (const element of group.element ?? []) {
      if (
        element.code &&
        element.target?.some(
          (target) =>
            target.code === coding.code && target.equivalence !== 'unmatched'
        )
      ) {
        const mapped: Coding = { system: group.source, code: element.code }
        if (element.display) {
          mapped.display = element.display
        }
        add(mapped)
      }
    }
  }
  return family
}
